/**
 * Org-safe facade over the generated share-requests client.
 *
 * A share request is the ask-side counterpart of a resource grant: a user
 * without access to a resource (today: an agent session) files one, and
 * the resource owner or an org admin approves it — opening a grant — or
 * declines it. Each hook takes a branded {@link OrgId} so an org-scoped
 * call without an active org is a compile error (see `./org-id.ts`). The
 * org rides on the `x-organization-id` header, so the id's role here is
 * cache partitioning: every key is prefixed with the org.
 */
import { useQueryClient } from '@tanstack/react-query';
import {
  useApproveShareRequest,
  useCreateShareRequest,
  useDeclineShareRequest,
  useListShareRequests,
} from '../generated/share-requests';
import type { ListShareRequestsParams } from '../generated/model/listShareRequestsParams';
import type { OrgId } from './org-id';

/**
 * The wire shape a share request arrives in. The shared `ShareRequest`
 * types its ids as prefix-checked template literals for the server
 * boundary; client consumers deal in already-serialized API data, which
 * the generated client types as plain (pattern-documented) strings.
 */
export type { ShareRequest } from '@runloop/reflex-client/react/model/shareRequest';

export const shareRequestsKey = (org: OrgId, params?: ListShareRequestsParams) =>
  params ? (['share-requests', org, params] as const) : (['share-requests', org] as const);

/** List the share requests visible to the caller, newest first. */
export function useShareRequests(org: OrgId, params?: ListShareRequestsParams) {
  return useListShareRequests(params, {
    query: { queryKey: shareRequestsKey(org, params) },
  });
}

/** Ask a resource's owner to share it (idempotent), invalidating the org's request lists. */
export function useCreateShareRequestMutation(org: OrgId) {
  const qc = useQueryClient();
  return useCreateShareRequest({
    mutation: {
      onSuccess: () => void qc.invalidateQueries({ queryKey: ['share-requests', org] }),
    },
  });
}

/** Approve a request (opens a grant to the requester), invalidating request and grant lists. */
export function useApproveShareRequestMutation(org: OrgId) {
  const qc = useQueryClient();
  return useApproveShareRequest({
    mutation: {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ['share-requests', org] });
        void qc.invalidateQueries({ queryKey: ['resource-grants', org] });
      },
    },
  });
}

/** Decline a request, invalidating the org's request lists. */
export function useDeclineShareRequestMutation(org: OrgId) {
  const qc = useQueryClient();
  return useDeclineShareRequest({
    mutation: {
      onSuccess: () => void qc.invalidateQueries({ queryKey: ['share-requests', org] }),
    },
  });
}
